import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface WeekViewProps {
  currentDate: Date;
  selectedDay: number;
  onDayClick: (day: number) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  isMobile: boolean;
  eventDays: number[]; // Days that have events
}

export const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  selectedDay,
  onDayClick,
  onPrevWeek,
  onNextWeek,
  isMobile,
  eventDays,
}) => {
  // Track swipe direction for animation
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);

  // Touch tracking for manual swipe detection
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate current week days
  const getWeekDays = () => {
    const curr = new Date(currentDate);
    const first = curr.getDate() - curr.getDay(); // First day of week (Sunday)

    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(curr);
      day.setDate(first + i);
      return {
        date: day.getDate(),
        month: day.getMonth(),
        year: day.getFullYear(),
        fullDate: day,
        isCurrentMonth: day.getMonth() === currentDate.getMonth(),
      };
    });
  };

  const weekDays = getWeekDays();
  const weekDayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  const isToday = (day: number, month: number, year: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    );
  };

  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  // Handle touch move
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  // Handle touch end - detect swipe and trigger navigation
  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;

    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe left → Next week
        setSwipeDirection('left');
        setTimeout(() => {
          onNextWeek();
          setSwipeDirection(null);
        }, 150);
      } else {
        // Swipe right → Previous week
        setSwipeDirection('right');
        setTimeout(() => {
          onPrevWeek();
          setSwipeDirection(null);
        }, 150);
      }
    }

    // Reset touch tracking
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Animation variants for week transition
  const weekVariants = {
    enter: (direction: 'left' | 'right' | null) => ({
      x: direction === 'left' ? 100 : direction === 'right' ? -100 : 0,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: 'left' | 'right' | null) => ({
      x: direction === 'left' ? -100 : direction === 'right' ? 100 : 0,
      opacity: 0,
    }),
  };

  // Generate unique key for current week
  const weekKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${Math.floor(currentDate.getDate() / 7)}`;

  return (
    <div className="bg-white overflow-hidden">
      {/* Week Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Esta semana</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={onPrevWeek}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors active:bg-gray-200"
            aria-label="Semana anterior"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <button
            onClick={onNextWeek}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors active:bg-gray-200"
            aria-label="Semana siguiente"
          >
            <ChevronRight size={20} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Swipe hint indicator - only on mobile */}
      {isMobile && (
        <div className="flex justify-center py-1.5 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-medium">
            <ChevronLeft size={12} />
            <span>Desliza para cambiar semana</span>
            <ChevronRight size={12} />
          </div>
        </div>
      )}

      {/* Week Days Grid - Swipeable on mobile */}
      <div
        ref={containerRef}
        className="overflow-hidden"
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchMove={isMobile ? handleTouchMove : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        <AnimatePresence mode="wait" custom={swipeDirection}>
          <motion.div
            key={weekKey}
            custom={swipeDirection}
            variants={weekVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="grid grid-cols-7 gap-1 p-3 select-none"
          >
            {weekDays.map((day, index) => {
              const isSelected = day.date === selectedDay && day.isCurrentMonth;
              const hasEvent = eventDays.includes(day.date) && day.isCurrentMonth;
              const isDayToday = isToday(day.date, day.month, day.year);

              return (
                <div key={index} className="flex flex-col items-center">
                  {/* Day name */}
                  <div className="text-xs font-medium text-gray-400 mb-2">
                    {weekDayNames[index]}
                  </div>

                  {/* Day number - Touch target 44px */}
                  <motion.button
                    onClick={() => day.isCurrentMonth && onDayClick(day.date)}
                    className={`
                      w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold
                      transition-colors relative
                      ${isSelected
                        ? 'bg-[#5D1826] text-white shadow-md'
                        : isDayToday
                        ? 'bg-[#5D1826]/10 text-[#5D1826]'
                        : day.isCurrentMonth
                        ? 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                        : 'text-gray-300'
                      }
                    `}
                    whileTap={{ scale: 0.9 }}
                    disabled={!day.isCurrentMonth}
                  >
                    {day.date}

                    {/* Event dot */}
                    {hasEvent && !isSelected && (
                      <span className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-[#D4A658]" />
                    )}
                  </motion.button>
                </div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
